import { useState } from 'react';

import {
  Alert,
  Badge,
  Container,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';

import { LogoPaper } from '@/components';
import { useMonitoringAlerts } from '@/hooks/useMonitoringAlerts';
import {
  MONITORED_SUBJECT_STATUS_LABELS,
  monitoringAlertStateLabel,
  monitoringComponentLabel,
} from '@/types/monitoring';

const PAGE_SIZE = 50;

/** Highest first — an analyst works down, so the ordering is the workflow. */
const SEVERITY_COLOURS: Record<string, string> = {
  HIGH: 'red',
  MEDIUM: 'orange',
  LOW: 'gray',
  UNKNOWN: 'yellow',
};

function severityBadge(severity: string | null) {
  // Null and UNKNOWN are different facts and both matter: null means nothing
  // has classified it yet, UNKNOWN means classification ran and failed. Either
  // way it routes as if high, so neither may render as quietly low-risk.
  if (!severity) {
    return (
      <Badge color="yellow" variant="light">
        unclassified
      </Badge>
    );
  }
  return (
    <Badge color={SEVERITY_COLOURS[severity] ?? 'gray'} variant="light">
      {severity.toLowerCase()}
    </Badge>
  );
}

export function MonitoringAlertsPage() {
  const [page, setPage] = useState(1);
  const [state, setState] = useState<string | null>('new');
  const [severity, setSeverity] = useState<string | null>(null);
  const [includeSeeded, setIncludeSeeded] = useState(false);

  const {
    page: result,
    isLoading,
    error,
  } = useMonitoringAlerts({
    page,
    pageSize: PAGE_SIZE,
    state: state ?? undefined,
    severity: severity ?? undefined,
    includeSeeded,
  });

  // Every filter change resets to page 1: staying on page 4 of a narrower set
  // shows an empty table that reads as "no alerts" rather than "no page 4".
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(1);
  };

  const total = result?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const rows = result?.items ?? [];

  return (
    <Stack>
      <Container size="xl" w="100%">
        <LogoPaper>
          <Stack>
            <Title order={2}>Monitoring alerts</Title>

            <Group>
              <Select
                label="State"
                value={state}
                onChange={(value) => changeFilter(() => setState(value))}
                clearable
                data={[
                  { value: 'new', label: 'New' },
                  { value: 'confirmed', label: 'Confirmed' },
                  { value: 'dismissed_no_match', label: 'Not our subject' },
                  { value: 'dismissed_immaterial', label: 'Not material' },
                  { value: 'resolved', label: 'Resolved' },
                ]}
              />
              <Select
                label="Severity"
                value={severity}
                onChange={(value) => changeFilter(() => setSeverity(value))}
                clearable
                data={[
                  { value: 'HIGH', label: 'High' },
                  { value: 'MEDIUM', label: 'Medium' },
                  { value: 'LOW', label: 'Low' },
                  { value: 'UNKNOWN', label: 'Unknown' },
                ]}
              />
              <Switch
                label="Include report history"
                description="Findings the enrolling report already showed the client"
                checked={includeSeeded}
                onChange={(event) => {
                  const { checked } = event.currentTarget;
                  changeFilter(() => setIncludeSeeded(checked));
                }}
                mt="lg"
              />
            </Group>

            {error && (
              <Alert color="red" title="Could not load alerts">
                {error.message}
              </Alert>
            )}

            {isLoading && !result && <Loader size="sm" />}

            {result && rows.length === 0 && <Text c="dimmed">No alerts match these filters.</Text>}

            {rows.length > 0 && (
              <Table.ScrollContainer minWidth={900}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Subject</Table.Th>
                      <Table.Th>Check</Table.Th>
                      <Table.Th>Severity</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>State</Table.Th>
                      <Table.Th>Last seen</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((row) => (
                      <Table.Tr key={row.id}>
                        <Table.Td>
                          <Group gap="xs">
                            {row.subjectName}
                            {row.seeded && (
                              <Badge size="xs" variant="outline" color="gray">
                                from report
                              </Badge>
                            )}
                            {row.subjectStatus !== 'active' && (
                              <Tooltip
                                label={`Monitoring for this subject is ${MONITORED_SUBJECT_STATUS_LABELS[
                                  row.subjectStatus
                                ].toLowerCase()}, so the client is not told about this alert.`}
                              >
                                <Badge
                                  size="xs"
                                  variant="light"
                                  color="orange"
                                  data-testid={`alert-subject-inactive-${row.id}`}
                                >
                                  client not told ·{' '}
                                  {MONITORED_SUBJECT_STATUS_LABELS[row.subjectStatus].toLowerCase()}
                                </Badge>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>{monitoringComponentLabel(row.scopeComponentSlug)}</Table.Td>
                        <Table.Td>{severityBadge(row.severity)}</Table.Td>
                        <Table.Td>
                          {row.taxonomyCategory ? row.taxonomyCategory.replace(/-/g, ' ') : '—'}
                        </Table.Td>
                        <Table.Td>{monitoringAlertStateLabel(row.state)}</Table.Td>
                        <Table.Td>
                          {row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleDateString() : '—'}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}

            {totalPages > 1 && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of{' '}
                  {total} alerts
                </Text>
                <Pagination value={page} onChange={setPage} total={totalPages} />
              </Group>
            )}
          </Stack>
        </LogoPaper>
      </Container>
    </Stack>
  );
}
